import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, type OnInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subject, takeUntil } from 'rxjs';
import {
    type ProductCardDataFormType,
    type ProductCardFormType,
    type ProductCardWidgetData,
    type VariantFormGroup,
} from '@web/widgets/product-card/type';
import {
    imageFormControl,
    parameterFormGroup,
    productCardFormGroup,
    propertyFormGroup,
    sectionFormGroup,
    variantFormGroup,
} from '@web/widgets/product-card/form/product-card-widget.form';
import { FormBuilder, type FormControl, type UntypedFormGroup } from '@angular/forms';
import { type ProductCard, ProductType } from '@common/types/product-card.type';
import { getUuid } from '@common/helpers';
import { ProductCardHttpService } from '@web/core/services/REST/product-card.http.service';
import { TranslateModule } from '@ngx-translate/core';
import { ProductVariantComponent } from './product-variant/product-variant.component';

import { ProductPriceComponent } from './product-price/product-price.component';
import { ProductDetailComponent } from './product-detail/product-detail.component';
import { ProductImagesComponent } from './product-images/product-images.component';
import { ProductNameComponent } from './product-name/product-name.component';
import { WidgetsFacade } from '@web/store/page-structure/widgets/widgets.facade';
import type { WebLegacyWidgetEntity } from '@web/types';

@Component({
    selector: 'web-create-product-modal',
    templateUrl: './create-product-modal.component.html',
    styleUrls: ['./create-product-modal.component.less'],
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: true,
    imports: [
        ProductNameComponent,
        ProductImagesComponent,
        ProductDetailComponent,
        ProductPriceComponent,
        ProductVariantComponent,
        TranslateModule,
    ],
})
export class CreateProductModalComponent implements OnInit {
    private readonly bsModalRef = inject(BsModalRef);
    private readonly fb = inject(FormBuilder);
    protected readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly productCardHttpService = inject(ProductCardHttpService);
    private readonly widgetsFacade: WidgetsFacade = inject(WidgetsFacade);

    public onClose: Subject<ProductCard>;
    public widgetData: ProductCardWidgetData;
    public isEditCard: boolean = false;
    private initialData: { name: string; url: string } = { name: null, url: null };
    public productForm: UntypedFormGroup;
    public digitalProductLink: FormControl<string> = this.fb.control('');
    public widgetId: string;
    public hasPropertiesInEditError: boolean = false;

    private destroy$ = new Subject<void>();

    public ngOnInit(): void {
        this.initForm();
        this.setFormData();
        this.onClose = new Subject();

        if (this.widgetId) {
            this.widgetsFacade
                .widgetById$(this.widgetId)
                .pipe(takeUntil(this.destroy$))
                .subscribe((widget: WebLegacyWidgetEntity) => {
                    if (!(widget?.data as any)?.element) return;

                    const newImages = (widget.data as any).element?.images ?? [];

                    if (this.images?.length !== newImages.length) {
                        this.element.controls.images.clear({ emitEvent: false });
                        newImages.forEach(() =>
                            this.element.controls.images.push(imageFormControl(null), { emitEvent: false })
                        );
                        this.images.patchValue(newImages, { emitEvent: false });
                        this.changeDetectorRef.detectChanges();
                        return;
                    }

                    if (this.images?.length === newImages.length) {
                        this.images.patchValue(newImages, { emitEvent: false });
                        this.changeDetectorRef.detectChanges();
                    }
                });
        }
    }

    private initForm(): void {
        this.productForm = this.fb.group({
            element: productCardFormGroup(),
        });
        this.element.controls.sections.push(sectionFormGroup());
    }

    private setFormData(): void {
        if (!this.isEditCard) {
            this.productForm.patchValue({ element: { id: getUuid() } }, { emitEvent: false });
        } else {
            this.setFormArrayValue();

            this.productForm.patchValue({ element: this.widgetData?.element }, { emitEvent: false });
            if (this.isDigitalProductType) {
                this.initialData.name = this.widgetData?.element.name;
                this.getDigitalProductData();
            }
        }
    }

    private setImages() {
        if (this.widgetData?.element?.images.length !== this.element.controls.images.length) {
            this.element.controls.images.clear();
            this.widgetData?.element?.images.forEach(() => {
                this.element.controls.images.push(imageFormControl(null), { emitEvent: false });
            });
        }
    }

    private setSections() {
        if (!this.widgetData?.element?.sections.length) {
            this.element.controls.sections.clear();
            this.element.controls.sections.push(sectionFormGroup(), { emitEvent: false });
            return;
        }

        this.element.controls.sections.clear({ emitEvent: false });

        this.widgetData?.element?.sections.forEach((section) => {
            const formGroup = sectionFormGroup();

            if (section.parameters.length !== formGroup.controls.parameters.length) {
                formGroup.controls.parameters.clear({ emitEvent: false });
                section.parameters.forEach(() => {
                    formGroup.controls.parameters.push(parameterFormGroup(), { emitEvent: false });
                });
            }

            this.element.controls.sections.push(formGroup, { emitEvent: false });
        });
    }

    private setVariants() {
        if (this.widgetData?.element?.variants?.length !== this.element.controls.variants.length) {
            this.element.controls.variants.clear({ emitEvent: false });

            this.widgetData?.element?.variants?.forEach((variant) => {
                const formGroup: VariantFormGroup = variantFormGroup();

                variant?.images?.forEach(() => {
                    formGroup.controls.images.push(imageFormControl(null), { emitEvent: false });
                });

                this.element.controls.variants.push(formGroup, { emitEvent: false });
            });
        }
    }

    private setProperties() {
        if (this.widgetData?.element?.properties?.length !== this.element.controls.properties.length) {
            this.element.controls.properties.clear({ emitEvent: false });

            this.widgetData?.element?.properties?.forEach(() => {
                const formGroup = propertyFormGroup();

                this.element.controls.properties.push(formGroup, { emitEvent: false });
            });
        }
    }

    private setFormArrayValue() {
        this.setImages();
        this.setSections();
        this.setVariants();
        this.setProperties();
    }

    private checkVariantsProperty() {
        if (!this.variants?.length) return;

        this.variants.controls.forEach((variant) => {
            if (!variant.value.properties?.length) {
                variant.get('properties').patchValue(variant.value.name.split('/'));
            }
        });
    }

    public onConfirm(): void {
        const hasPropertiesInEdit = this.properties.controls.some((property) => property.value?.isEdit);

        this.hasPropertiesInEditError = hasPropertiesInEdit;
        if (hasPropertiesInEdit) {
            this.changeDetectorRef.detectChanges();
            return;
        }

        if (this.productName.invalid || this.digitalProductLink.invalid) {
            this.productName.markAsTouched();
            this.productName.markAsDirty();
            this.digitalProductLink.markAsTouched();
            this.digitalProductLink.markAsDirty();

            this.changeDetectorRef.detectChanges();

            return;
        }

        this.checkVariantsProperty();
        this.clearEmptyFields();

        if (this.isDigitalProductType) {
            this.setDigitalProductUrl();
        } else {
            this.onClose.next(this.element.getRawValue());
            this.bsModalRef.hide();
        }
    }

    public onCancel(): void {
        this.onClose.next(null);
        this.bsModalRef.hide();
    }

    private clearEmptyFields() {
        const value = this.sections.getRawValue();
        this.sections.clear();

        value.forEach((section, index) => {
            const params = [];
            section.parameters.forEach((parameter) => {
                if (parameter.value || parameter.parameter) {
                    params.push(parameter);
                }
            });

            if (params.length || section.name) {
                this.sections.push(sectionFormGroup(), { emitEvent: false });
                if (params.length !== this.sections.at(index).value.parameters.length && params.length) {
                    this.sections.at(index).controls.parameters.clear({ emitEvent: false });
                    params.forEach(() => {
                        this.sections.at(index).controls.parameters.push(parameterFormGroup());
                    });
                }
                this.sections.at(index).patchValue({ name: section.name, parameters: params });
            }
        });

        this.sections.controls.filter((section) => {
            section.controls.parameters.controls.filter(
                (parameter) => parameter.value.value && parameter.value.parameter
            );

            return section.value.parameters.length && section.value.name;
        });
    }

    private setDigitalProductUrl() {
        this.productCardHttpService
            .setDigitalProductUrl({ name: this.productName.value, url: this.digitalProductLink.value })
            .subscribe(({ data }) => {
                this.digitalProductId.setValue(data.id);
                this.onClose.next(this.element.getRawValue());
                this.bsModalRef.hide();
            });
    }

    private updateDigitalProductData() {
        this.productCardHttpService
            .updateDigitalProductData(
                { name: this.productName.value, url: this.digitalProductLink.value },
                this.digitalProductId.value
            )
            .subscribe(() => {
                this.onClose.next(this.element.getRawValue());
                this.bsModalRef.hide();
            });
    }

    private getDigitalProductData() {
        this.productCardHttpService.getDigitalProductData(this.digitalProductId.value).subscribe(({ data }) => {
            this.initialData.url = data.url;
            this.digitalProductLink.setValue(data.url);
        });
    }

    public get element(): ProductCardDataFormType['element'] {
        return this.productForm.get('element') as ProductCardDataFormType['element'];
    }

    public get productName(): ProductCardFormType['name'] {
        return this.element.get('name') as ProductCardFormType['name'];
    }

    public get description(): ProductCardFormType['description'] {
        return this.element.get('description') as ProductCardFormType['description'];
    }

    public get shortDescription(): ProductCardFormType['shortDescription'] {
        return this.element.get('shortDescription') as ProductCardFormType['shortDescription'];
    }

    public get images(): ProductCardFormType['images'] {
        return this.element.get('images') as ProductCardFormType['images'];
    }

    public get price(): ProductCardFormType['price'] {
        return this.element.get('price') as ProductCardFormType['price'];
    }

    public get oldPrice(): ProductCardFormType['oldPrice'] {
        return this.element.get('oldPrice') as ProductCardFormType['oldPrice'];
    }

    public get quantity(): ProductCardFormType['quantity'] {
        return this.element.get('quantity') as ProductCardFormType['quantity'];
    }

    public get unit(): ProductCardFormType['unit'] {
        return this.element.get('unit') as ProductCardFormType['unit'];
    }

    public get vendorCode(): ProductCardFormType['vendorCode'] {
        return this.element.get('vendorCode') as ProductCardFormType['vendorCode'];
    }

    public get sections(): ProductCardFormType['sections'] {
        return this.element.get('sections') as ProductCardFormType['sections'];
    }

    public get detailControls(): {
        unit: ProductCardFormType['unit'];
        vendorCode: ProductCardFormType['vendorCode'];
        quantity: ProductCardFormType['quantity'];
        sections: ProductCardFormType['sections'];
        type: ProductCardFormType['type'];
        digitalProductLink: FormControl<string>;
    } {
        return {
            sections: this.sections,
            vendorCode: this.vendorCode,
            unit: this.unit,
            quantity: this.quantity,
            type: this.type,
            digitalProductLink: this.digitalProductLink,
        };
    }

    public get variants(): ProductCardFormType['variants'] {
        return this.element.get('variants') as ProductCardFormType['variants'];
    }

    public get properties(): ProductCardFormType['properties'] {
        return this.element.get('properties') as ProductCardFormType['properties'];
    }

    public get type(): ProductCardFormType['type'] {
        return this.element.get('type') as ProductCardFormType['type'];
    }

    public get digitalProductId(): ProductCardFormType['digitalProductId'] {
        return this.element.get('digitalProductId') as ProductCardFormType['digitalProductId'];
    }

    public get isDigitalProductType(): boolean {
        return this.type.value === ProductType.digital;
    }
}
